import { useEffect, useMemo, useState } from "react"

import { format } from "date-fns"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { INVALID_UK_TIME_ERROR, ukWallClockToInstant } from "@/constants/sheriffOptions"
import { useDepots } from "@/hooks/useDepots"
import { usePublishRewave } from "@/hooks/usePublishRewave"
import { useRewavePool } from "@/hooks/useRewavePool"
import { Analytics, AnalyticsEvent } from "@/services/analytics"

import DatePicker from "../rewaving/DatePicker"
import PriorityList from "../rewaving/PriorityList"
import WaveSlots from "../rewaving/WaveSlots"
import { Priority, Wave, WaveDistribution } from "../rewaving/types"

const DEFAULT_WAVE_COUNT = 10

const START_TIMES = Array.from({ length: 24 * 4 }, (_, i) => {
  const h = Math.floor(i / 4)
  const m = (i % 4) * 15
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
})

const INTERVALS = [
  { value: "5", label: "5 minutes" },
  { value: "10", label: "10 minutes" },
  { value: "15", label: "15 minutes" },
  { value: "20", label: "20 minutes" },
  { value: "25", label: "25 minutes" },
  { value: "30", label: "30 minutes" },
]

const computeDistributions = (priorities: Priority[], waves: Wave[]): Wave[] => {
  const remaining = priorities.map((p) => p.pool)

  return waves.map((wave) => {
    const distributions: WaveDistribution[] = []
    let driversNeeded = wave.drivers

    for (let i = 0; i < priorities.length && driversNeeded > 0; i++) {
      if (remaining[i] <= 0) {
        continue
      }
      const take = Math.min(driversNeeded, remaining[i])
      distributions.push({ key: priorities[i].key, label: priorities[i].label, count: take })
      remaining[i] -= take
      driversNeeded -= take
    }

    return { ...wave, distributions }
  })
}

const ReWavingContent = () => {
  const { data: depots = [], isLoading: isDepotsLoading } = useDepots()
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false)
  const [depot, setDepot] = useState("")
  const [date, setDate] = useState<Date | undefined>(new Date())
  const [startTime, setStartTime] = useState("07:00")
  const [waveInterval, setWaveInterval] = useState(15)
  const [priorities, setPriorities] = useState<Priority[]>([])
  const [waves, setWaves] = useState<Wave[]>([])

  useEffect(() => {
    if (!depot && depots.length > 0) {
      setDepot(depots[0].id)
    }
  }, [depot, depots])

  const {
    data: poolData,
    isLoading: isPoolLoading,
    error: poolError,
  } = useRewavePool(depot, date ? format(date, "yyyy-MM-dd") : null)
  const publishMutation = usePublishRewave()

  useEffect(() => {
    if (!poolData) {
      return
    }

    if (poolData.pools) {
      const poolMap = new Map(poolData.pools.map((p) => [p.key, p]))
      const orderedPriorities: Priority[] = poolData.priorityOrder
        .filter((key) => poolMap.has(key))
        .map((key) => {
          const pool = poolMap.get(key)!
          return { key, label: pool.label, pool: pool.count }
        })
      setPriorities(orderedPriorities)
    } else {
      setPriorities([])
    }
  }, [poolData])

  useEffect(() => {
    setWaves(Array.from({ length: DEFAULT_WAVE_COUNT }, () => ({ drivers: 0, distributions: [] })))
  }, [depot, date])

  const wavesWithDistributions = useMemo(
    () => computeDistributions(priorities, waves),
    [priorities, waves],
  )

  const handleAddWave = () => {
    setWaves((prev) => [...prev, { drivers: 0, distributions: [] }])
  }

  const handleRemoveWave = (index: number) => {
    setWaves((prev) => prev.filter((_, i) => i !== index))
  }

  const handleDriversChange = (index: number, drivers: number) => {
    setWaves((prev) => prev.map((w, i) => (i === index ? { ...w, drivers } : w)))
  }

  const buildPublishPayload = (dateStr: string) => {
    const baseDate = ukWallClockToInstant(dateStr, startTime)

    const priorityOrder = priorities.map((p) => p.key)

    const publishWaves = wavesWithDistributions.map((wave, index) => {
      const waveDate = new Date(baseDate.getTime() + waveInterval * index * 60000)
      const drivers: Record<string, number> = {}
      for (const dist of wave.distributions) {
        drivers[dist.key] = dist.count
      }
      return { drivers, start_time: waveDate.toISOString() }
    })

    return {
      depot_id: depot,
      date: dateStr,
      start_time: baseDate.toISOString(),
      interval_mins: waveInterval,
      priority_order: priorityOrder,
      waves: publishWaves,
    }
  }

  const validateBeforePublish = () => {
    if (isPoolLoading) {
      return false
    }
    const depotName = depots.find((d) => d.id === depot)?.name
    if (!depotName || !date) {
      toast.error("Publish failed", { description: "Please select a depot and date." })
      return false
    }

    const totalPool = priorities.reduce((sum, p) => sum + p.pool, 0)
    if (totalPool === 0) {
      toast.error("Publish failed", { description: "There are no blocks to publish." })
      return false
    }
    const allocated = Math.min(
      waves.reduce((sum, w) => sum + w.drivers, 0),
      totalPool,
    )
    const remaining = totalPool - allocated
    if (remaining > 0) {
      toast.error("Not all drivers allocated", {
        description: `${remaining} drivers aren't assigned to a wave yet.`,
      })
      return false
    }

    if (Number.isNaN(ukWallClockToInstant(format(date, "yyyy-MM-dd"), startTime).getTime())) {
      toast.error("Publish failed", { description: INVALID_UK_TIME_ERROR })
      return false
    }

    return true
  }

  const handlePublish = () => {
    if (!validateBeforePublish()) {
      return
    }

    if (poolData?.lastPublication) {
      setShowOverwriteDialog(true)
      return
    }

    publishWaves()
  }

  const publishWaves = () => {
    if (!date) {
      return
    }
    const depotName = depots.find((d) => d.id === depot)?.name
    const payload = buildPublishPayload(format(date, "yyyy-MM-dd"))

    publishMutation.mutate(payload, {
      onSuccess: (result) => {
        Analytics.track(AnalyticsEvent.REWAVING_SAVED, {
          depot: depotName,
          date: format(date!, "dd/MM/yy"),
          waveCount: waves.length,
          shiftsUpdated: result.shiftsUpdated,
        })

        toast.success("Waves published", {
          description: `Waves have been successfully set for ${depotName} - ${format(date!, "dd/MM/yy")} (${result.shiftsUpdated} blocks updated)`,
        })
      },
      onError: (error) => {
        toast.error("Publish failed", {
          description: error.message,
        })
      },
    })
  }

  const renderPriorities = () => {
    if (isPoolLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-palette-jungleGreen500">Loading priorities...</p>
        </div>
      )
    }
    if (poolError) {
      return (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-indicators-red">
            Failed to load priorities: {poolError.message}
          </p>
        </div>
      )
    }
    if (priorities.length > 0) {
      return <PriorityList priorities={priorities} onReorder={setPriorities} />
    }
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-palette-jungleGreen500">
          No blocks available for the selected depot and date.
        </p>
      </div>
    )
  }

  if (isDepotsLoading || !depot) {
    return (
      <div className="flex h-[calc(100vh-12rem)] items-center justify-center">
        <p className="text-sm text-palette-jungleGreen500">Loading...</p>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-12rem)] flex-col">
      <h2 className="mb-4 text-lg font-semibold text-palette-jungleGreen900">Block waves</h2>

      <div className="flex min-h-0 flex-1 rounded bg-white p-6 shadow">
        <div className="flex w-1/2 min-h-0 flex-col overflow-hidden pr-6">
          <div className="flex h-full w-full flex-col gap-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-palette-jungleGreen900">
                  Depot
                </label>
                <Select value={depot} onValueChange={setDepot}>
                  <SelectTrigger>
                    <SelectValue placeholder="Depot" />
                  </SelectTrigger>
                  <SelectContent>
                    {depots.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-palette-jungleGreen900">
                  Date
                </label>
                <DatePicker date={date} onDateChange={setDate} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-palette-jungleGreen900">
                  Start time
                </label>
                <Select value={startTime} onValueChange={setStartTime}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {START_TIMES.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-palette-jungleGreen900">
                  Intervals
                </label>
                <Select
                  value={String(waveInterval)}
                  onValueChange={(v) => setWaveInterval(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVALS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">{renderPriorities()}</div>
          </div>
        </div>

        <div className="w-px self-stretch bg-palette-jungleGreen200" />

        <div className="flex w-1/2 min-h-0 flex-col overflow-hidden pl-6">
          <WaveSlots
            waves={wavesWithDistributions}
            priorities={priorities}
            startTime={startTime}
            interval={waveInterval}
            onAddWave={handleAddWave}
            onRemoveWave={handleRemoveWave}
            onDriversChange={handleDriversChange}
            onSave={handlePublish}
            isPublishing={publishMutation.isPending}
          />
        </div>
      </div>

      <Dialog open={showOverwriteDialog} onOpenChange={setShowOverwriteDialog}>
        <DialogContent>
          <DialogHeader className="space-y-4">
            <DialogTitle className="text-palette-jungleGreen900">
              Waves already published for this day
            </DialogTitle>
            <DialogDescription className="text-palette-jungleGreen900">
              Waves for {date ? format(date, "dd/MM/yy") : ""} were already published. Continuing
              will apply the new plan for that day.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverwriteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowOverwriteDialog(false)
                publishWaves()
              }}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ReWavingContent
